import * as Sentry from '@sentry/nextjs';
import qs, { stringify } from 'qs';
import type { Attribute } from '@strapi/strapi';
import type { Got } from 'got/dist/source';
import type { KyInstance } from 'ky/distribution/types/ky';

import { hasAudiences, PrefixToLocaleMap, Project } from '@tmh-website/shared/const';
import { isKnowledgeCenterWhitepaperPage } from '@tmh-website/services/page.service';
import { sitePopulateOptions } from '@tmh-website/components/blocks-populate-options';
import { universalGetAudiences } from '@tmh-website/services/audience.service';
import {
  type GlobalData,
  isElectricityTariffPerson,
  isNavigationPageLinkGroup,
  isPerson,
  isStrapiImage,
  isStrapiVideo,
  isValidLinkField,
} from '@tmh-website/shared/cms';
import type {
  AnyPage,
  ContactSupport,
  LinkField,
  NavigationElement,
  NavigationHighlightLinkGroup,
  NavigationPageLink,
  NavigationPageLinkGroup,
  Partner,
  ResponseCollectionType,
  ResponseSingleType,
  Site,
} from '@tmh-website/shared/cms';
import type { Audience, LocalePrefix } from '@tmh-website/shared/const';

interface GetGlobalDataOptions {
  project: Project;
  locale: LocalePrefix;
}

/**
 * Fetch global data from Strapi
 * This is a universal function that is used to keep client and server side requests in sync
 */
export async function universalGetGlobalData(fetcherInstance: Got | KyInstance, options: GetGlobalDataOptions) {
  const endpointMap: { [key in Project]: string } = {
    [Project.EMobilityAssistant]: '',
    [Project.ElectricityTariff]: 'electricity-tariff/electricity-tariff-site',
    [Project.Energy]: 'energy/energy-site',
    [Project.Styleguide]: 'styleguide-site',
    [Project.Website]: 'site',
  };

  const site = await fetcherInstance
    .get(endpointMap[options.project], {
      searchParams: stringify({
        locale: PrefixToLocaleMap[options.locale],
        pagination: {
          withCount: false,
          pageSize: 50,
        },
        populate: {
          ...sitePopulateOptions,
        },
      }),
    })
    .json<ResponseSingleType<Site>>();

  (site.data as unknown as GlobalData).audiences = (await universalGetAudiences(fetcherInstance, options.locale)).data;

  return site.data!;
}

interface GetPartnersOptions {
  locale: LocalePrefix;
  page: AnyPage;
  project?: Project;
  customFilters?: any;
}

/**
 * Get partners data from Strapi
 * This is a universal function that is used to keep client and server side requests in sync
 */
export async function universalGetPartners(fetcherInstance: Got | KyInstance, props: GetPartnersOptions) {
  const filters =
    props.customFilters ?? isKnowledgeCenterWhitepaperPage(props.page)
      ? { whitepapers: props.page?.id }
      : { pages: props.page?.id };

  const endpointMap: { [key in Project]: string } = {
    [Project.ElectricityTariff]: 'electricity-tariff/electricity-tariff-partners',
    [Project.EMobilityAssistant]: '',
    [Project.Energy]: 'energy/energy-partners',
    [Project.Styleguide]: '',
    [Project.Website]: 'partners',
  };

  const partners = await fetcherInstance
    .get(endpointMap[props?.project ?? Project.Website], {
      searchParams: qs.stringify({
        locale: PrefixToLocaleMap[props.locale],
        pagination: {
          withCount: false,
          pageSize: 100,
        },
        populate: {
          logo: '*',
        },
        filters: filters,
      }),
    })
    .json<ResponseCollectionType<Partner>>();

  return partners.data;
}

interface GetServicesOptions {
  locale: LocalePrefix;
  page: AnyPage;
}

/**
 * Get services data from Strapi
 * This is a universal function that is used to keep client and server side requests in sync
 */
export async function universalGetServices(fetcherInstance: Got | KyInstance, props: GetServicesOptions) {
  const services = await fetcherInstance
    .get('services', {
      searchParams: qs.stringify({
        locale: PrefixToLocaleMap[props.locale],
        pagination: {
          withCount: false,
          pageSize: 100,
        },
        populate: {
          image: '*',
        },
        filters: { pages: props.page.id },
      }),
    })
    .json<ResponseCollectionType<Attribute.GetValues<'api::service.service'>>>();
  return services.data;
}

/**
 * Omit data from entities that are redundant to reduce page data size
 */
export function customParseJson(_key, value) {
  if (isStrapiVideo(value)) {
    delete (value as any).createdAt;
    delete (value as any).ext;
    delete (value as any).formats;
    delete (value as any).hash;
    delete (value as any).height;
    delete (value as any).name;
    delete (value as any).previewUrl;
    delete (value as any).provider;
    delete (value as any).provider_metadata;
    delete (value as any).size;
    delete (value as any).updatedAt;
    delete (value as any).width;
    value.type = 'video';
  } else if (isStrapiImage(value)) {
    delete (value as any).caption;
    delete (value as any).createdAt;
    delete (value as any).formats;
    delete (value as any).mime;
    delete (value as any).name;
    delete (value as any).previewUrl;
    delete (value as any).provider;
    delete (value as any).provider_metadata;
    delete (value as any).size;
    delete (value as any).updatedAt;
    value.type = 'image';
  } else if (isPerson(value)) {
    delete (value as any).createdAt;
    delete (value as any).updatedAt;
    delete (value as any).publishedAt;
    delete (value as any).locale;
  } else if (isElectricityTariffPerson(value)) {
    delete (value as any).createdAt;
    delete (value as any).updatedAt;
    delete (value as any).publishedAt;
    delete (value as any).locale;
  } else if (value?._type === 'link-field') {
    if (value.title === '') delete value.title;
    if (value.anchor === '') delete value.anchor;
    if (value.attributes.length === 0) delete value.attributes;
  }

  // Merge audiences into an array
  if (value?.audiences) {
    if (value.audiences && value.audiences.length > 0) {
      if (value.audiences[0].hasOwnProperty('key')) {
        value.audiences = value.audiences.map(audience => audience.key);
      }
    } else {
      value.audiences = [];
    }
  }

  /**
   * Convert any data - attributes nesting from their original format
   * `property: { data: [ id: number, attributes: { ... } ] }`
   * to a more compact format
   * `property:  [ { id: number, ...attributes } ]`.
   *
   * Global REST responses are converted as follows:
   * `data: [ { id: number, attributes: { ... } } ], meta: { ... }`
   * to
   * `data: [ { id: number, ...attributes } ], meta: { ... }`
   *
   * This is not only to save bandwidth and make it easier to work with, but also because the Strapi generated types are this way.
   */
  if (value?.data !== undefined) {
    let transformed: any;
    if (Array.isArray(value.data)) {
      transformed = value.data.map((entry: any) => ({ id: entry.id, ...entry.attributes }));
    } else if (value.data?.attributes) {
      transformed = { id: value.data.id, ...value.data.attributes };
    } else if (value.data === null) {
      transformed = null;
    }

    if (value?.meta) {
      // This is a top level collection response
      value.data = transformed;
    } else {
      // This is a nested response (e.g. inside a component)
      value = transformed;
    }
  }

  return value;
}

function hasFittingAudience(audiences: Audience[] | undefined, audience) {
  return audiences === undefined || audiences.includes(audience);
}

/**
 * Removes navigation elements that are not visible to the given audience.
 * Should only be used in environments that actually use audiences.
 */
export function filterNavigationByAudience(navigation: NavigationElement[], audience: Audience): NavigationElement[] {
  const newNavigation: NavigationElement[] = [];
  navigation.forEach(element => {
    if (isNavigationPageLinkGroup(element)) {
      const newChildren =
        element.children?.filter(
          child => isValidLinkField(child.page) && hasFittingAudience(child.page?.relevantAudiences, audience)
        ) ?? [];
      if (
        newChildren?.length > 0 ||
        (isValidLinkField(element.link.page) && hasFittingAudience(element.link.page?.relevantAudiences, audience))
      ) {
        if (newChildren?.length > 0) {
          // Group with fitting audience and with children
          newNavigation.push({ ...element, children: newChildren } as NavigationPageLinkGroup);
        } else {
          // Group with fitting audience and without children that gets converted to a single link
          newNavigation.push(element.link as NavigationPageLink);
        }
      }
    } else {
      if (isValidLinkField(element.page) && hasFittingAudience(element.page?.relevantAudiences, audience)) {
        // Single page link with fitting audience
        newNavigation.push(element);
      }
    }
  });
  return newNavigation;
}

/**
 * Removes navigation highlights that are not visible to the given audience
 */
export function filterNavigationHighlightsByAudience(navigation: NavigationHighlightLinkGroup[], audience: Audience) {
  const visibleGroups = (navigation ?? []).filter(element => {
    return (
      element.children?.some(
        child =>
          isValidLinkField(child.page) &&
          (child.page?.relevantAudiences === undefined || child.page.relevantAudiences.includes(audience))
      ) ||
      (isValidLinkField(element.page) &&
        (element.page?.relevantAudiences === undefined || element.page.relevantAudiences.includes(audience)))
    );
  });

  return visibleGroups.map(group => {
    const visibleChildren = (group.children ?? []).filter(
      child =>
        isValidLinkField(child.page) &&
        (child.page?.relevantAudiences === undefined || child.page.relevantAudiences.includes(audience))
    );
    return { ...group, children: visibleChildren.length > 0 ? visibleChildren : undefined };
  });
}

/**
 * Removes footer links that are not visible to the given audience
 */
export function filterFooterLinksByAudience(
  navigation: Attribute.GetValues<'navigation.footer-link-group'>[],
  audience: Audience | null,
  locale: LocalePrefix,
  project: Project
) {
  if (!audience || !navigation) {
    return [];
  }

  const visibleGroups = (navigation ?? []).filter(element => {
    if (!hasAudiences(locale, project)) {
      return true;
    }

    return (
      element.children?.some(
        child =>
          isValidLinkField(child.link) &&
          (child.link?.relevantAudiences === undefined || child.link.relevantAudiences.includes(audience))
      ) ||
      (isValidLinkField(element.link) &&
        (element.link?.relevantAudiences === undefined || element.link.relevantAudiences.includes(audience)))
    );
  });

  return visibleGroups.map(group => {
    const visibleChildren = (group.children ?? []).filter(child => {
      if (!isValidLinkField(child.link)) {
        return false;
      } else if (!hasAudiences(locale, project)) {
        return true;
      } else {
        return child.link?.relevantAudiences === undefined || child.link.relevantAudiences.includes(audience);
      }
    });

    return { ...group, children: visibleChildren.length > 0 ? visibleChildren : undefined };
  });
}

/**
 * Removes footer logos that are not visible to the given audience
 */
export function filterFooterLogosByAudience(
  logosGroup: Attribute.GetValues<'navigation.footer-logos'> | undefined,
  audience: Audience | null,
  locale: LocalePrefix,
  project: Project
) {
  if (!logosGroup?.partnerLogosEntries || !audience) {
    return [];
  }

  return logosGroup?.partnerLogosEntries.filter(entry => {
    if (!hasAudiences(locale, project)) {
      return true;
    }

    return (
      !(entry.link as LinkField) ||
      !(entry.link as LinkField).relevantAudiences ||
      (entry.link as LinkField).relevantAudiences!.length === 0 ||
      (entry.link as LinkField).relevantAudiences!.includes(audience)
    );
  });
}

export interface ApiErrorResponse extends Response {
  payload?: {
    data: null;
    error: {
      /**
       * Status code
       * @example 403
       */
      status: number;
      /**
       * Type of error
       * @example "ForbiddenError"
       */
      name: string;
      /**
       * Error message
       * @example "Invalid email-address / Passwort-Kombination."
       */
      message: string;
    };
  };
}

/**
 * Get the contact & support data from Strapi
 * This is a universal function that is used to keep client and server side requests in sync
 */
export async function universalGetContactSupport(fetcherInstance: Got | KyInstance, options: { locale: LocalePrefix }) {
  const { data } = await fetcherInstance
    .get('contact-support', {
      searchParams: stringify({
        locale: PrefixToLocaleMap[options.locale],
        populate: {
          contactSupportCategories: {
            populate: {
              contactSupportFaqEntries: { populate: '*' },
              contactSupportInfoEntries: { populate: '*' },
            },
          },
        },
      }),
    })
    .json<ResponseSingleType<ContactSupport>>();

  if (!data) {
    const error = new Error('Contact support data not found');
    Sentry.captureException(error);
    throw error;
  }

  return data;
}
